import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useApi } from '../../hooks/useApi';
import { PageWrapper, LoadingSpinner } from '../../components';
import ProfileEditModal from '../../components/user/ProfileEditModal';
import { AdminPortalSection } from '../../components/user';
import { useAuth as useAuthActions } from '../../hooks/useAuth';
import { isStandardApiResponse } from '../../types/api';
import '../../styles/pages/mypage.css';
import bg001 from '../../assets/images/bg-001.jpg';
import bg002 from '../../assets/images/bg-002.jpg';

// 新しいuser_data_management APIレスポンス形式
interface UserUsageStats {
  user_id: number;
  username: string;
  total_storage_bytes: number;
  total_storage_mb: number;
  total_storage_gb: number;
  app_count: number;
  apps: Array<{
    app_name: string;
    display_name: string;
    storage_bytes: number;
    storage_mb: number;
    storage_gb: number;
    last_activity: string;
  }>;
  limits: {
    storage_limit_bytes: number;
    storage_limit_gb: number;
    plan_name: string;
    display_name: string;
    source: string;
  };
  usage_percentage: {
    storage: number;
  };
}

// 旧analytics API形式（削除済み - user_data_management APIに移行）
// interface UserDetailedStats { ... }

/**
 * マイページコンポーネント
 * ユーザープロフィール管理画面
 */
const MyPage: React.FC = () => {
  const { user, isAuthenticated, loading, isAdmin, validateToken } = useAuth();
  const { execute: apiCall, error: apiError } = useApi();
  const { access3DAnalyzer } = useAuthActions();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  // 統計データ関連の状態
  const [userStats, setUserStats] = useState<any | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [hasTriedFetch, setHasTriedFetch] = useState(false);
  
  // 実行状態管理（StrictMode対応）
  const isRunning = useRef(false);

  // 緊急停止機能（現在は使用していない）
  // const emergencyStop = () => {
  //   console.log('🚨 緊急停止が実行されました');
  //   setStatsError('緊急停止しました。ページを再読み込みしてください。');
  // };

  // 開発環境でのみファイル識別ログ
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('🎯 MyPage.tsx が読み込まれました');
    }
  }, []);

  // デバッグ用ログ


  // ユーザー統計データを取得（StrictMode対応版）
  const fetchUserStats = async () => {
    // 基本的なガード条件（重複実行防止）
    if (!user?.id || statsLoading || hasTriedFetch) {
      if (process.env.NODE_ENV === 'development') {
        console.log('🚫 統計データ取得をスキップ:', { 
          hasUserId: !!user?.id, 
          statsLoading, 
          hasTriedFetch
        });
      }
      return;
    }

    // 既に実行中の場合もスキップ
    if (isRunning.current) {
      if (process.env.NODE_ENV === 'development') {
        console.log('🚫 統計データ取得は既に実行中です');
      }
      return;
    }

    isRunning.current = true; // 実行フラグを設定
    
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('🔄 統計データ取得開始:', { 
          userId: user.id, 
          timestamp: new Date().toISOString() 
        });
      }
      
      setStatsLoading(true);
      setStatsError(null);
      
      const token = localStorage.getItem('authToken');
      const cleanToken = token ? token.replace(/"/g, '') : '';
      
      if (!cleanToken) {
        throw new Error('認証トークンがありません');
      }
      
      // 1. サブスクリプション詳細取得
      const subscriptionResponse = await apiCall(`/api/subscription/current/`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${cleanToken}`
        }
      });
      
      // 2. 使用状況取得
      const usageResponse = await apiCall(`/api/user-data/users/${user.id}/usage/`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${cleanToken}`
        }
      });
      
      if (process.env.NODE_ENV === 'development') {
        console.log('📋 サブスクリプションレスポンス受信完了');
        console.log('📋 使用量レスポンス受信完了');
      }
      
      // サブスクリプション情報の処理
      let subscriptionData: any = null;
      if ((isStandardApiResponse(subscriptionResponse) && subscriptionResponse.status === 'success') ||
          (subscriptionResponse && typeof subscriptionResponse === 'object' && 'plan' in subscriptionResponse)) {
        subscriptionData = isStandardApiResponse(subscriptionResponse) ? 
          subscriptionResponse.data as any : 
          subscriptionResponse as any;
        
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ サブスクリプション情報取得成功');
        }
      } else {
        // サブスクリプション情報が取得できない場合のフォールバック
        if (process.env.NODE_ENV === 'development') {
          console.warn('⚠️ サブスクリプション情報取得失敗');
        }
        setStatsError('サブスクリプション情報の取得に失敗しました');
        return;
      }

      // 使用量情報の処理
      if ((isStandardApiResponse(usageResponse) && usageResponse.status === 'success') ||
          (usageResponse && typeof usageResponse === 'object' && 'user_id' in usageResponse)) {
        const usageData = isStandardApiResponse(usageResponse) ? 
          usageResponse.data as UserUsageStats : 
          usageResponse as UserUsageStats;
        
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ 使用量データ取得成功');
        }
        
        const combinedStats = {
            user_info: {
              id: user.id,
              username: user.username,
              email: user.email
            },
            usage_breakdown: {
              total_storage_mb: usageData.total_storage_mb || 0,
              total_storage_gb: usageData.total_storage_gb || 0,
              total_files: 0, // user_data_managementでは管理していない
              total_projects: 0, // user_data_managementでは管理していない
              total_data_count: 0, // user_data_managementでは管理していない
              usage_percentage: usageData.usage_percentage?.storage || 0,
              plan_limits: {
                max_storage_gb: subscriptionData.plan?.max_storage_gb || 1,
                max_storage_mb: (subscriptionData.plan?.max_storage_gb || 1) * 1024,
                max_projects: subscriptionData.plan?.max_projects,
                max_data_per_project: subscriptionData.plan?.max_data_per_project,
                plan_name: subscriptionData.plan?.display_name || 'LITEプラン',
                is_unlimited_projects: !subscriptionData.plan?.max_projects,
                is_unlimited_data: !subscriptionData.plan?.max_data_per_project,
                price_monthly: subscriptionData.plan?.price_monthly || 0
              },
              project_limit_info: {
                can_create: true, // TODO: 必要に応じて計算
                limit: subscriptionData.plan?.max_projects,
                current: 0, // TODO: 実際のプロジェクト数を取得
                remaining: subscriptionData.plan?.max_projects
              },
              services: usageData.apps?.map(app => ({
                name: app.app_name,
                status: 'active',
                usage: app.storage_mb,
                limit: undefined
              })) || []
            },
            recent_activities: [], // TODO: 必要に応じて実装
            monthly_summary: {
              uploads_count: 0, // TODO: 必要に応じて実装
              uploaded_mb: usageData.total_storage_mb || 0,
              month_name: new Date().toLocaleDateString('ja-JP', { month: 'long' }),
              projects_accessed: 0, // TODO: 必要に応じて実装
              total_data_count: 0
            }
          };
          
          setUserStats(combinedStats);
          setStatsError(null);
          setHasTriedFetch(true);
          
          if (process.env.NODE_ENV === 'development') {
            console.log('✅ サブスクリプション・使用量データを正常に取得しました');
          }
      } else {
        // 使用量データが取得できない場合でも、サブスクリプション情報は表示
        if (process.env.NODE_ENV === 'development') {
          console.warn('⚠️ 使用量データ取得失敗、サブスクリプション情報のみで表示');
        }
        
        const combinedStats = {
          user_info: {
            id: user.id,
            username: user.username,
            email: user.email
          },
          usage_breakdown: {
            total_storage_mb: 0,
            total_storage_gb: 0,
            total_files: 0,
            total_projects: 0,
            total_data_count: 0,
            usage_percentage: 0,
            plan_limits: {
              max_storage_gb: subscriptionData.plan?.max_storage_gb || 1,
              max_storage_mb: (subscriptionData.plan?.max_storage_gb || 1) * 1024,
              max_projects: subscriptionData.plan?.max_projects,
              max_data_per_project: subscriptionData.plan?.max_data_per_project,
              plan_name: subscriptionData.plan?.display_name || 'LITEプラン',
              is_unlimited_projects: !subscriptionData.plan?.max_projects,
              is_unlimited_data: !subscriptionData.plan?.max_data_per_project,
              price_monthly: subscriptionData.plan?.price_monthly || 0
            },
            project_limit_info: {
              can_create: true,
              limit: subscriptionData.plan?.max_projects,
              current: 0,
              remaining: subscriptionData.plan?.max_projects
            },
            services: []
          },
          recent_activities: [],
          monthly_summary: {
            uploads_count: 0,
            uploaded_mb: 0,
            month_name: new Date().toLocaleDateString('ja-JP', { month: 'long' }),
            projects_accessed: 0,
            total_data_count: 0
          }
        };
        
        setUserStats(combinedStats);
        setStatsError(null);
        setHasTriedFetch(true);
        
        if (process.env.NODE_ENV === 'development') {
          console.log('⚠️ 使用量データなしでサブスクリプション情報のみ表示');
        }
      }
    } catch (error) {
      const errorMsg = `統計データの取得に失敗しました: ${error}`;
      setStatsError(errorMsg);
      if (process.env.NODE_ENV === 'development') {
        console.error('❌ 統計データ取得例外:', error);
      }
    } finally {
      setStatsLoading(false);
      isRunning.current = false; // 実行フラグをリセット
      if (process.env.NODE_ENV === 'development') {
        console.log('🏁 統計データ取得完了 - 実行フラグリセット');
      }
    }
  };

  // Stripe決済処理
  const handleStripeUpgrade = () => {
    // STUDIOプランの料金を表示（現在のプランではなくSTUDIOプランの料金）
    alert('Stripe決済システムに移動します。\n\nSTUDIOプランへの登録を開始します。\n\n※ 現在は開発中のため、実際の決済は行われません。');
  };

  const handleStripeManage = () => {
    alert('Stripeカスタマーポータルに移動します。\n\nサブスクリプションの管理・変更・解約が可能です。\n\n※ 現在は開発中のため、実際の管理画面は表示されません。');
  };

  // 統計データ取得（StrictMode対応版）
  useEffect(() => {
    let isCancelled = false; // クリーンアップフラグ

    if (isAuthenticated && user?.id && !hasTriedFetch && !statsLoading) {
      if (process.env.NODE_ENV === 'development') {
        console.log('🚀 useEffect: 統計データ取得を実行');
      }
      
      // 非同期関数を定義
      const fetchData = async () => {
        if (isCancelled) return; // キャンセルされている場合は実行しない
        await fetchUserStats();
      };
      
      fetchData();
    }

    // クリーンアップ関数
    return () => {
      isCancelled = true;
      if (process.env.NODE_ENV === 'development') {
        console.log('🧹 useEffect: クリーンアップ実行 - リクエストキャンセル');
      }
    };
  }, [isAuthenticated, user?.id]); // hasTriedFetch, statsLoadingは依存関係に含めない

  // ストレージ表示用フォーマット
  const formatStorage = (mb: number) => {
    if (mb >= 1024) {
      return `${(mb / 1024).toFixed(1)}GB`;
    }
    return `${mb}MB`;
  };

  // プレミアムサービス（ログイン必要・無料プランデフォルト）
  const premiumServices = [
    {
      id: 4,
      title: '3D Analyzer',
      description: '3Dスキャンデータを解析し、デジタル計測を支援します。',
      appLink: `${process.env.REACT_APP_3D_ANALYZER_URL || 'http://localhost:3001'}`,
      image: bg001,
      icon: 'fas fa-cube'
    },
    {
      id: 5,
      title: 'Panel Layout',
      description: 'DXFデータで指定した領域に、最適なパネル配置を提案します。',
      appLink: '#panel-layout',
      image: bg002,
      icon: 'fas fa-th-large'
    }
  ];

  // ユーザーが認証されていない場合のリダイレクト
  if (!isAuthenticated && !loading) {
    return (
      <PageWrapper 
        title="アクセス拒否"
        titleClassName="text-danger"
      >
        <div className="text-center py-5">
          <div className="alert alert-warning" role="alert">
            <h4 className="alert-heading">
              ログインが必要です
            </h4>
            <p>このページにアクセスするにはログインしてください。</p>
            <hr />
            <button 
              className="btn btn-primary"
              data-bs-toggle="modal" 
              data-bs-target="#loginModal"
            >
              ログイン
            </button>
          </div>
        </div>
      </PageWrapper>
    );
  }

  // ローディング中の表示
  if (loading) {
    return (
      <PageWrapper title="マイページ">
        <LoadingSpinner message="ユーザー情報を読み込み中..." />
      </PageWrapper>
    );
  }

  // プロフィール保存処理
  const handleSaveProfile = async (formData: {
    username: string;
    email: string;
    first_name: string;
    last_name: string;
    display_name: string;
    organization: string;
  }) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('認証が必要です');
      }

      await apiCall('/api/auth/user/update/', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token.replace(/"/g, '')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      // useApiフックのerror状態をチェック
      if (apiError) {
        throw new Error(apiError);
      }

      // ユーザー情報を更新
      await validateToken();
      
      // 成功通知
      const toast = document.createElement('div');
      toast.className = 'success-toast';
      toast.innerHTML = '✅ プロフィールを更新しました';
      document.body.appendChild(toast);
      
      setTimeout(() => {
        toast.style.animation = 'slideOut 0.3s ease-in';
        setTimeout(() => document.body.removeChild(toast), 300);
      }, 3000);
      
    } catch (error: unknown) {
      console.error('Profile update error:', error);
      const errorMessage = error instanceof Error ? error.message : 'プロフィールの更新に失敗しました';
      throw new Error(errorMessage);
    }
  };

  return (
    <div className="mypage-container">
      <PageWrapper 
        title="マイページ"
      >
        {/* 統計データ読み込み状態 */}
        {statsLoading && (
          <div className="alert alert-info mb-3">
            <div>
              <i className="fas fa-spinner fa-spin me-2"></i>
              統計データを読み込み中...
            </div>
          </div>
        )}
        {/* 管理者ポータルセクション（管理者のみ表示） */}
        {isAdmin() && (
          <AdminPortalSection />
        )}

        {/* お知らせセクション */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="profile-card p-4">
              <h5 className="mb-3">お知らせ</h5>
              <ul className="notification-list mb-2">
                <li className="notification-item my-2">
                  <span className="notification-date">2024.12.21</span>
                  <span className="notification-content">
                    プロフィール編集機能がリニューアルされました。編集ボタンからモーダルで編集できます。
                  </span>
                </li>
                <li className="notification-item my-2">
                  <span className="notification-date">2024.12.15</span>
                  <span className="notification-content">
                    マイページのデザインを更新しました。より見やすくなりました。
                  </span>
                </li>
                <li className="notification-item my-2">
                  <span className="notification-date">2024.12.10</span>
                  <span className="notification-content">
                    セキュリティ強化のため、定期的なパスワード変更をお勧めします。
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="row d-flex align-items-stretch">
          {/* 登録情報 (1/3) */}
          <div className="col-lg-4">
            <div className="profile-card p-3 d-flex flex-column">
              <h5 className="mb-2">登録情報</h5>
              
              <div className="profile-info-modern flex-grow-1">
                <div className="profile-field">
                  <div className="field-label">ユーザー名</div>
                  <div className="field-value">
                    {user?.username || '未設定'}
                  </div>
                </div>

                <div className="profile-field">
                  <div className="field-label">名前</div>
                  <div className="field-value">
                    {user?.last_name && user?.first_name 
                      ? `${user.last_name} ${user.first_name}`
                      : '未設定'
                    }
                  </div>
                </div>

                <div className="profile-field">
                  <div className="field-label">メールアドレス</div>
                  <div className="field-value">
                    {user?.email || '未設定'}
                  </div>
                </div>

                <div className="profile-field">
                  <div className="field-label">所属組織</div>
                  <div className="field-value">
                    {user?.organization || '未設定'}
                  </div>
                </div>
              </div>

              {/* ボタンエリア - 右下 */}
              <div className="mt-auto pt-3 d-flex justify-content-end gap-2 border-top profile-button-area">
                <button 
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => setIsEditModalOpen(true)}
                >
                  編集
                </button>
                <button 
                  className="btn btn-outline-secondary btn-sm"
                  data-bs-toggle="modal"
                  data-bs-target="#changePasswordModal"
                >
                  パスワード変更
                </button>
              </div>
            </div>
          </div>

          {/* サブスクプラン (1/3) */}
          <div className="col-lg-4">
            <div className="profile-card p-3 d-flex flex-column">
              <h5 className="mb-3">サブスクプラン</h5>
              
              <div className="subscription-card">
                <div className="plan-info p-3 bg-light rounded mb-3">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <h6 className="mb-0 text-primary">
                      {userStats?.usage_breakdown?.plan_limits?.plan_name || 'LITEプラン'}
                    </h6>
                    <span className="badge bg-success">アクティブ</span>
                  </div>
                  
                  {/* プラン詳細情報 */}
                  {(() => {
                    if (process.env.NODE_ENV === 'development') {
                      console.log('🎯 UI レンダリング時のデータ構造チェック完了');
                    }
                    return null;
                  })()}
                  {userStats?.usage_breakdown?.plan_limits && (
                    <div className="plan-details">
                      {/* 基本制限情報 */}
                      <div className="plan-limits-section mb-3">
                        <h6 className="text-muted mb-2 border-bottom pb-1">
                          <i className="fas fa-chart-bar me-2"></i>利用制限
                        </h6>
                        
                        <div className="d-flex justify-content-between mb-2">
                          <span className="text-muted">
                            <i className="fas fa-database me-1"></i>ストレージ容量
                          </span>
                          <span className="fw-medium">{userStats.usage_breakdown.plan_limits.max_storage_gb}GB</span>
                        </div>
                        
                        <div className="d-flex justify-content-between mb-2">
                          <span className="text-muted">
                            <i className="fas fa-folder me-1"></i>プロジェクト数
                          </span>
                          <span className="fw-medium">
                            {userStats.usage_breakdown.plan_limits.is_unlimited_projects ? 
                              <span className="text-primary">無制限</span> : 
                              `${userStats.usage_breakdown.plan_limits.max_projects}個まで`
                            }
                          </span>
                        </div>
                        
                        <div className="d-flex justify-content-between mb-2">
                          <span className="text-muted">
                            <i className="fas fa-cube me-1"></i>データ/プロジェクト
                          </span>
                          <span className="fw-medium">
                            {userStats.usage_breakdown.plan_limits.is_unlimited_data ? 
                              <span className="text-primary">無制限</span> : 
                              `${userStats.usage_breakdown.plan_limits.max_data_per_project}件まで`
                            }
                          </span>
                        </div>
                      </div>

                      {/* 料金情報 */}
                      <div className="plan-pricing-section mb-3">
                        <h6 className="text-muted mb-2 border-bottom pb-1">
                          <i className="fas fa-credit-card me-2"></i>料金
                        </h6>
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="text-muted">月額料金</span>
                          <div className="text-end">
                            <span className="fw-bold text-success fs-5">
                              {userStats.usage_breakdown.plan_limits.price_monthly === 0 ? 
                               '無料' : 
                               `¥${userStats.usage_breakdown.plan_limits.price_monthly?.toLocaleString() || '0'}`}
                            </span>

                          </div>
                        </div>
                      </div>


                    </div>
                  )}
                </div>
                
                {/* Stripe決済ボタン（カード外） */}
                {userStats?.usage_breakdown?.plan_limits && (
                  <div className="stripe-actions text-center">
                    {userStats.usage_breakdown.plan_limits.plan_name === 'LITEプラン' ? (
                      // LITEプランの場合：サブスクリプション管理
                      <div>
                        <button 
                          className="btn btn-primary px-3 py-1"
                          onClick={handleStripeUpgrade}
                          title="サブスクリプション管理"
                          style={{minWidth: '60px'}}
                        >
                          <i className="fab fa-stripe" style={{fontSize: '1.8rem'}}></i>
                        </button>
                        <div className="text-muted small mt-2">
                          サブスクリプション管理
                        </div>
                        <div className="text-muted small">
                          決済サービス<a href="https://stripe.com/jp" target="_blank" rel="noopener noreferrer" className="text-decoration-none" style={{color: '#635bff'}}>stripe</a>を使用しています
                        </div>
                      </div>
                    ) : userStats.usage_breakdown.plan_limits.plan_name === 'STUDIOプラン' ? (
                      // STUDIOプランの場合：サブスクリプション管理・変更
                      <div>
                        <button 
                          className="btn btn-primary px-3 py-1"
                          onClick={handleStripeManage}
                          title="サブスクリプション管理・変更"
                          style={{minWidth: '60px'}}
                        >
                          <i className="fab fa-stripe" style={{fontSize: '1.8rem'}}></i>
                        </button>
                        <div className="text-muted small mt-2">
                          サブスクリプション管理・変更
                        </div>
                        <div className="text-muted small">
                          決済サービス<a href="https://stripe.com/jp" target="_blank" rel="noopener noreferrer" className="text-decoration-none" style={{color: '#635bff'}}>stripe</a>を使用しています
                        </div>
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* 使用状況統計 (1/3) */}
          <div className="col-lg-4">
            <div className="profile-card p-3 d-flex flex-column">
              <h5 className="mb-3">使用状況</h5>
              
              {statsLoading ? (
                <div className="text-center py-4">
                  <div className="spinner-border spinner-border-sm text-primary" role="status">
                    <span className="visually-hidden">読み込み中...</span>
                  </div>
                </div>
              ) : userStats ? (
                <div className="usage-stats">
                  {/* ストレージ使用量 */}
                  {userStats.usage_breakdown && (
                    <div className="mb-3">
                      <div className="d-flex justify-content-between mb-2">
                        <span className="text-muted">ストレージ</span>
                        <span className="fw-medium">
                          {formatStorage(userStats.usage_breakdown.total_storage_mb || 0)} / 
                          {userStats.usage_breakdown.plan_limits?.max_storage_gb || 1}GB
                        </span>
                      </div>
                      <div className="progress progress-thin">
                        <div 
                          className={`progress-bar ${
                            (userStats.usage_breakdown.usage_percentage || 0) > 80 ? 'bg-danger' : 'bg-primary'
                          }`}
                          role="progressbar" 
                          style={{width: `${Math.min(userStats.usage_breakdown.usage_percentage || 0, 100)}%`}}
                        ></div>
                      </div>
                      <small className="text-muted">
                        {Math.round(userStats.usage_breakdown.usage_percentage || 0)}% 使用中
                      </small>
                    </div>
                  )}
                  
                  {/* アプリ別使用量 */}
                  {userStats.usage_breakdown.services && userStats.usage_breakdown.services.length > 0 && (
                    <div className="mb-3">
                      <h6 className="text-muted mb-2">アプリ別使用量</h6>
                      {userStats.usage_breakdown.services.map((service: any, index: number) => (
                        <div key={index} className="d-flex justify-content-between mb-1">
                          <span className="text-muted small">{service.name}</span>
                          <span className="fw-medium small">{formatStorage(service.usage || 0)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : statsError ? (
                <div className="text-center py-4">
                  <div className="text-danger mb-2">
                    <i className="fas fa-exclamation-triangle"></i>
                  </div>
                  <small className="text-muted">{statsError}</small>
                  {/* 🚨 再試行ボタンを完全無効化して無限ループを防止 */}
                  <div className="mt-2">
                    <button 
                      className="btn btn-sm btn-outline-secondary"
                      disabled={true}
                      title="開発中のため一時的に無効化されています"
                    >
                      再試行（無効化中）
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-4 text-muted">
                  <small>統計データが取得できませんでした</small>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ディバイダー */}
        <div className="row">
          <div className="col-12">
            <hr className="my-5" />
          </div>
        </div>

        {/* プレミアムサービスセクション */}
        <div className="mb-5">
          <div className="mb-4">
            <div className="d-flex align-items-center mb-2">
              <h4 className="mb-0">Premium</h4>
            </div>
          </div>
          
          <div className="bg-light rounded p-4 border">
            <div className="row g-4">
              {premiumServices.map(service => (
                <div key={service.id} className="col-lg-4">
                  {/* 統合されたサービスカード */}
                  <div 
                    className="card h-100 shadow-sm border-0 overflow-hidden"
                    style={{ 
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
                    }}
                    onClick={async () => {
                      if (service.id === 4) {
                        // 3D Analyzerの場合はJWT認証付きでアクセス
                        await access3DAnalyzer();
                      } else {
                        // その他のサービスは準備中メッセージ
                        alert('このサービスは準備中です。しばらくお待ちください。');
                      }
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.25)';
                    }}
                  >
                    <div className="position-relative" style={{ height: '200px' }}>
                      <img 
                        src={service.image} 
                        className="card-img-top w-100 h-100" 
                        style={{ objectFit: 'cover' }}
                        alt={service.title}
                      />
                      <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                           style={{ 
                             background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.2))',
                             opacity: 0,
                             transition: 'opacity 0.3s ease'
                           }}
                           onMouseEnter={(e) => (e.target as HTMLElement).style.opacity = '1'}
                           onMouseLeave={(e) => (e.target as HTMLElement).style.opacity = '0'}>
                        <div className="text-white fs-1">
                          <i className={service.icon}></i>
                        </div>
                      </div>
                    </div>
                    
                    <div className="card-body d-flex flex-column p-4">
                      <h5 className="card-title text-dark mb-3" style={{ fontSize: '1.4rem', lineHeight: '1.3' }}>
                        {service.title}
                        {service.id === 4 && (
                          <small className="text-muted ms-2">
                            <i className="fas fa-external-link-alt"></i>
                          </small>
                        )}
                      </h5>
                      <p className="card-text text-muted flex-grow-1" style={{ fontSize: '0.95rem', lineHeight: '1.6' }}>
                        {service.description}
                        {service.id === 4 && (
                          <span className="d-block mt-2 text-primary small fw-medium">
                            <i className="fas fa-mouse-pointer me-1"></i>
                            クリックして3D Analyzerを開く
                          </span>
                        )}
                      </p>
                      
                                             {/* ユーザー情報エリア - カード内に統合 */}
                       <div className="mt-3 pt-3 border-top">
                         <h6 className="text-uppercase fw-bold text-muted mb-3 small">利用状況</h6>
                         <div>
                                                  {service.id === 4 ? (
                          // 3D Analyzer の情報
                          <>
                            <div className="d-flex justify-content-between border-bottom pb-2">
                              <span className="text-muted small">最終アクセス</span>
                              <span className="fw-medium">2024/12/20</span>
                            </div>
                            <div className="d-flex justify-content-between border-bottom pb-2">
                              <span className="text-muted small">解析済みファイル</span>
                              <span className="fw-medium">3 ファイル</span>
                            </div>
                            <div className="d-flex justify-content-between">
                              <span className="text-muted small">使用容量</span>
                              <span className="fw-medium">45.2 MB</span>
                            </div>
                          </>
                        ) : (
                          // Panel Layout の情報
                          <>
                            <div className="d-flex justify-content-between border-bottom pb-2">
                              <span className="text-muted small">最終アクセス</span>
                              <span className="fw-medium text-muted">未利用</span>
                            </div>
                            <div className="d-flex justify-content-between border-bottom pb-2">
                              <span className="text-muted small">作成プロジェクト</span>
                              <span className="fw-medium">0 個</span>
                            </div>
                            <div className="d-flex justify-content-between">
                              <span className="text-muted small">使用容量</span>
                              <span className="fw-medium">0 MB</span>
                            </div>
                          </>
                        )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* プロフィール編集モーダル */}
        {user && (
          <ProfileEditModal
            user={user}
            isOpen={isEditModalOpen}
            onClose={() => setIsEditModalOpen(false)}
            onSave={handleSaveProfile}
          />
        )}
      </PageWrapper>
    </div>
  );
};

export default MyPage; 